// Third Party
import React, { ReactElement, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// Components
import { Button, Loader, Switch, Typography, WarningPrompt } from 'components';
import { ButtonBar, TutorialButton, WidgetTable } from 'components/machine-health';

// API
import {
  useGetMachineConfiguratorDataQuery,
  useUpdateMachineConfiguratorDataMutation
} from 'api';

// Types
import { MachineHealthTabs, WidgetType } from 'types/protein';
import { WidgetTableDataItem } from 'types/machine-health';

// Constants
import { tutorialPlaceholderStepsData, WidgetTableColumnConfigs } from 'constants/machineConfig';

// Providers
import { useWidgetTableContext } from 'components/machine-health/configurator/WidgetTable/WidgetTableContext';
import { useLanguage } from 'providers';

// Helpers
import { IcoUndo } from 'icons/indexed-icons/IcoUndo';
import { ToastMsg } from 'common/components/Toast';

// Hooks
import { useCopyConfiguration } from '../../hooks';

// Common
import CopyConfigurationActions from '../../components/CopyConfigurationActions';

// Styling
const Container = styled.div<{ removePadding?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${({ removePadding }) => (removePadding ? '0' : '0 6rem')};
  width: 100%;
`;

const WidgetTableContainer = styled.div`
  display: flex;
  gap: 1.375rem;
  flex-direction: column;
  flex-grow: 1;
  padding-bottom: 1.375rem;
  width: 100%;
`;
interface Props {
  copyWidgetType?: WidgetType;
  handleCopyCallback?: (item: WidgetTableDataItem) => void;
  hasWidgets?: boolean;
  hideButtonRow?: boolean;
  isCopyTab?: boolean;
  isDirty: boolean;
  overrideMachineId?: string;
  removePadding?: boolean;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

const MachineOverview = ({
  copyWidgetType,
  handleCopyCallback,
  hasWidgets,
  hideButtonRow,
  isDirty,
  isCopyTab,
  overrideMachineId,
  removePadding,
  setIsDirty
}: Props): ReactElement => {
  const theme = useTheme();
  const { machineId } = useParams<{ machineId: string }>();
  const { localTableActive, localTableNames, localTableRows } = useWidgetTableContext();
  const { languageId } = useLanguage();
  const {
    data,
    isFetching: fetching,
    error
  } = useGetMachineConfiguratorDataQuery({
    machineId: overrideMachineId || machineId,
    labels: [MachineHealthTabs.Overview],
    languageId: languageId,
    showInactive: true
  });

  const [updateOverviewData, { isLoading: updating, error: errorUpdate }] =
    useUpdateMachineConfiguratorDataMutation();

  const [tabData, setTabData] = useState<WidgetTableDataItem | undefined>(data?.[0]);
  const [isActive, setIsActive] = useState(!!data?.[0]?.active);

  const {
    copiedConfiguration,
    copyModalOpen,
    copyFetching,
    updatingConfiguration,
    errorCopy,
    errorUpdateConfiguration,
    isViewConfirmCancel,
    setCopyModalOpen,
    setIsViewConfirmCancel,
    copyConfiguration,
    onCopyFromSelectedMachine,
    onClickDelete,
    onClickDeleteCopiedWidgets,
    openCopyModal
  } = useCopyConfiguration({
    tabData,
    setTabData,
    setIsDirty,
    machineHealthTabLabel: MachineHealthTabs.Overview,
    supportsClearWidgets: true
  });

  const saveInfo = async () => {
    // Do nothing if we have no tab data
    if (!tabData) {
      return;
    }

    // Reconstruct data to send to server
    const updatedOverviewTab = {
      ...tabData,
      active: isActive,
      members: localTableRows.find((row) => tabData.id === row.id)?.members ?? []
    };

    await updateOverviewData({
      machineId,
      widget: updatedOverviewTab,
      languageId: languageId
    })
      .unwrap()
      .then(() => {
        ToastMsg({ heading: 'Success', type: 'success', message: `Overview updated successfully` });
        setIsDirty(false);
      })
      .catch((error) => {
        ToastMsg({ heading: 'Error', type: 'error', message: `Failed to update overview` });
        console.error(error?.data?.detail);
      });
  };

  useEffect(() => {
    setTabData(data?.[0]);
    setIsActive(!!data?.[0]?.active);
  }, [data]);

  useEffect(() => {
    setIsDirty(false);
  }, []);

  const handleEnableClick = () => {
    setIsActive((prev) => !prev);
    setIsDirty(true);
  };

  const isFetching = fetching || updating || copyFetching || updatingConfiguration;
  const isError = error || errorUpdate || errorCopy || errorUpdateConfiguration;

  if (isFetching) {
    return <Loader />;
  }

  if (isError) {
    console.error(isError);
    <Typography color="negativeRed">Failed to load data</Typography>;
  }

  return (
    <Container removePadding={removePadding}>
      {!hideButtonRow && (
        <ButtonBar columnGap="1.5rem" title="" padding="1.375rem 0">
          {tabData && !copiedConfiguration && (
            <>
              <Button
                onClick={() => onClickDelete()}
                variant={'primary'}
                width="fit-content"
                className="btn-undo"
              >
                <span style={{ marginRight: '0.3rem', marginTop: '0.065rem' }}>
                  <IcoUndo width={'24'} fill={theme.colors.mediumBlue} />
                </span>
                Clear Configuration
              </Button>
              <Switch
                checked={isActive}
                handleDiameter={22}
                height={14}
                onChange={handleEnableClick}
                width={40}
                offColor={theme.colors.mediumGrey2}
                offHandleColor={theme.colors.mediumGrey3}
              />

              <Button
                disabled={!isDirty}
                onClick={() => saveInfo()}
                variant={'primary'}
                width="fit-content"
              >
                Save
              </Button>
            </>
          )}

          <CopyConfigurationActions
            copiedConfiguration={copiedConfiguration}
            copyConfiguration={copyConfiguration}
            copyModalOpen={copyModalOpen}
            hasWidgets={hasWidgets}
            onCopyFromSelectedMachine={onCopyFromSelectedMachine}
            openCopyModal={openCopyModal}
            setCopyModalOpen={setCopyModalOpen}
          />

          <TutorialButton stepsData={tutorialPlaceholderStepsData} />
        </ButtonBar>
      )}

      {tabData ? (
        <WidgetTableContainer>
          <WidgetTable
            columnConfigs={WidgetTableColumnConfigs}
            copyTable={{
              copyWidgetType,
              handleCopyCallback,
              isCopyTable: isCopyTab
            }}
            data={tabData.members}
            isDataLoading={isFetching}
            key={tabData.id}
            parent={{
              ...tabData,
              // Do not allow the table to be editable here, as this is a duplication of the logic elsewhere in the page
              editable: false,
              toggleActive: false
            }}
            displayOnlyRows={[
              {
                id: 'machine-image-id',
                key: uuidv4(),
                name: 'Image',
                type: 'Machine Image',
                widgetType: WidgetType.MachineImage
              }
            ]}
            setIsDirty={setIsDirty}
            shouldRenderTags={false}
          />
        </WidgetTableContainer>
      ) : (
        <Typography color="greyfont">No data found</Typography>
      )}
      <WarningPrompt
        helperText={'Are you sure you wish to delete widgets for this machine?'}
        isConfirmPrompt
        isVisible={isViewConfirmCancel}
        onCancelCallback={() => setIsViewConfirmCancel(false)}
        onConfirmCallback={onClickDeleteCopiedWidgets}
        title={'Delete Widgets'}
      />
    </Container>
  );
};

export default MachineOverview;
